import { Alerts, CardIcon, LoopButton, Tooltip, Typography } from '@loophealth/loop-ui-web-library';
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ArrowRightEmerald, EditIconGreen, PersonIcon, WalletIcon } from '../../../assets/img';
import { CashIcon, InfoIcon } from '../../../assets/img/CustomIcons';
import { ReduxState } from '../../../redux';
import { setModularCurrentStep } from '../../../redux/slices/ModularEnrolmentSlice';
import {
    selectModularFamily,
    selectModularPolicyId,
    selectModularSummary,
    selectModularWalletBalance
} from '../../../redux/slices/ModularEnrolmentSlice/selectors';
import { fetchModularFamily, fetchModularSummary } from '../../../redux/slices/ModularEnrolmentSlice/thunks';
import { ModularEnrolmentSteps } from '../../../redux/slices/ModularEnrolmentSlice/types';
import { BenefitPricingStructure, IAlacarte } from '../../../redux/slices/PolicyListSlice/types';
import theme from '../../../theme';
import { formatAmount, textPluralize } from '../../../utils/common';
import { SEGMENT_ACTIONS } from '../../../utils/constants/SegmentActionConstants';
import useSegment from '../../../utils/hooks/useSegment';
import { Loader } from '../../atoms';
import BenefitDetailsModal from '../BenefitDetailsModal';
import CoveredMembersModal from '../CoveredMembersModal';
import { getModularEnrolmentSummarySegmentProperties } from '../ModularEnrolmentFooter/utils';
import ModularEnrolmentHeader from '../ModularEnrolmentHeader';
import PolicyBenefitsModal from '../PolicyBenefitsModal';
import {
    StyledEmiDeductionBox,
    StyledModularEnrolmentSummary,
    StyledModularEnrolmentSummaryCard,
    StyledModularEnrolmentSummaryCardContent,
    StyledModularSummaryAlacarteText,
    StyledModularSummaryColumn,
    StyledModularSummaryColumnRight,
    StyledModularSummaryHorizontalDivider,
    StyledModularSummaryHorizontalDottedDivider,
    StyledModularSummaryPlanCTA,
    StyledModularSummaryPlanCTASeperator,
    StyledModularSummaryPlanCTAWrapper,
    StyledModularSummaryPlanItem,
    StyledModularSummaryPlanItemInfo,
    StyledModularSummaryPlanText,
    StyledModularSummaryPlanWallet,
    StyledModularSummaryPremiumWrapper,
    StyledModularSummaryTooltip,
    StyledModularSummaryTooltipItem,
    StyledModularSummaryTotalHorizontalDivider,
    StyledSelectedPlanHeader
} from './styles';
import { IModularSummaryViewFamily } from './types';
import { getModularSummaryAlacarteSubtext, getModularSummaryFamilyBenefits } from './utils';
import { FlagsmithFlags, useFeatureFlag, useFeatureFlagValue } from '../../../utils/flagsmith';

const ModularEnrolmentSummary: React.FC = () => {
    const dispatch = useDispatch();
    const trackClick = useSegment('click');
    const trackPage = useSegment('page');
    const userId = useSelector((state: ReduxState) => state.user.userData.data?.userId || '');
    const policyId = useSelector(selectModularPolicyId);
    const summaryData = useSelector(selectModularSummary);
    const family = useSelector(selectModularFamily);
    const balance = useSelector(selectModularWalletBalance);
    const paymentInstallments = useFeatureFlagValue(FlagsmithFlags.PAYMENT_INSTALLMENTS) as string;
    const isPaymentInstallmentsEnabled = useFeatureFlag(FlagsmithFlags.PAYMENT_INSTALLMENTS);

    const [viewPlanDetails, setViewPlanDetails] = React.useState(false);
    const [viewBenefitDetails, setViewBenefitDetails] = React.useState<IAlacarte>();
    const [viewCoveredMembers, setViewCoveredMembers] = React.useState<IModularSummaryViewFamily>();

    const isFamilyLoading = family.loading;
    const summary = summaryData.data;
    const isLoading = summaryData.loading;
    const errorMessage = summaryData.error?.message;
    const toUseWallet = summary?.priceSummary.walletDeducted ? summary.priceSummary.walletDeducted > 0 : false;
    const familyBenefits = useMemo(
        () => getModularSummaryFamilyBenefits(summary?.alacarte ?? [], family.data ?? []),
        [summary, family.data]
    );

    const setCurrentStep = (step: ModularEnrolmentSteps) => {
        dispatch(setModularCurrentStep(step));
    };
    const moveToBasePlanScreen = () => {
        setCurrentStep(ModularEnrolmentSteps.BASE_PLAN);
    };
    const moveToBenefitsScreen = () => {
        setCurrentStep(ModularEnrolmentSteps.HEALTHCARE_BENEFITS);
    };

    const handleViewCoveredMembers = () => {
        const clickAction = SEGMENT_ACTIONS.CLICK.VIEW_COVERED_MEMBERS;
        trackClick({
            name: clickAction.name,
            properties: {
                ...clickAction.properties,
                policy_id: policyId,
                plan_name: summary?.modular?.name
            }
        });
        dispatch(fetchModularFamily({ userId, policyId }));
        setViewCoveredMembers({ name: 'base policy', family: family.data || [] });
    };

    const handleViewDetails = () => {
        const clickAction = SEGMENT_ACTIONS.CLICK.VIEW_DETAILS;
        trackClick({
            name: clickAction.name,
            properties: {
                ...clickAction.properties,
                policy_id: policyId,
                plan_name: summary?.modular?.name,
                plan_id: summary?.modular?.id
            }
        });
        setViewPlanDetails(true);
    };

    const handleViewAlacarteDetails = (b: IAlacarte) => {
        setViewBenefitDetails(b);
    };

    const handleAlaCarteViewMembers = (b: IAlacarte) => {
        setViewCoveredMembers({
            name: b.name,
            family: familyBenefits[b.id]?.family ?? []
        });
    };

    useEffect(() => {
        dispatch(fetchModularSummary({ userId, policyId }));
    }, []);

    useEffect(() => {
        if (summary) {
            const action = SEGMENT_ACTIONS.PAGE.PURCHASE_SUMMARY_PAGE;
            trackPage({
                ...action,
                properties: { ...action.properties, ...getModularEnrolmentSummarySegmentProperties(summary, balance) }
            });
        }
    }, [summary]);

    const salaryDeducted = summary?.priceSummary?.salaryDeducted ?? 0;
    const paymentInstallmentsNumber = parseInt(paymentInstallments, 10);
    const installmentsCount = isNaN(paymentInstallmentsNumber) ? 3 : paymentInstallmentsNumber;
    const emiAmount = formatAmount(Math.ceil(salaryDeducted / installmentsCount));
    const hasInstallmentsText = isNaN(paymentInstallmentsNumber) && !!paymentInstallments.length;

    return (
        <StyledModularEnrolmentSummary>
            <ModularEnrolmentHeader title="Purchase summary" subtitle="Review and confirm your plan selections" />
            <StyledModularEnrolmentSummaryCard>
                {isLoading ? (
                    <Loader />
                ) : errorMessage ? (
                    <Alerts.StatusAlert
                        variant="over"
                        text={
                            <Typography variant="medium" color="error">
                                {errorMessage}
                            </Typography>
                        }
                        alignItems="center"
                    />
                ) : summary ? (
                    <StyledModularEnrolmentSummaryCardContent>
                        <StyledSelectedPlanHeader>
                            <Typography variant="small" color="secondary">
                                Selected Base Policy
                            </Typography>
                            <LoopButton
                                variant="text"
                                size="small"
                                onClick={moveToBasePlanScreen}
                                iconSrc={EditIconGreen}
                            >
                                Change Plan
                            </LoopButton>
                        </StyledSelectedPlanHeader>
                        <StyledModularSummaryPlanItem>
                            <StyledModularSummaryPlanItemInfo>
                                <CardIcon imageUrl={summary.modular.logo} />
                                <StyledModularSummaryPlanCTAWrapper>
                                    <StyledModularSummaryPlanText>
                                        <Typography variant="large" weight="semiBold">
                                            {summary.modular.name}
                                        </Typography>
                                        <Typography variant="small" color="secondary">
                                            Sum insured {formatAmount(summary.modular.sumInsured)}
                                        </Typography>
                                    </StyledModularSummaryPlanText>
                                    <StyledModularSummaryPlanCTA>
                                        <LoopButton
                                            variant="text"
                                            size="small"
                                            onClick={handleViewCoveredMembers}
                                            iconSrc={PersonIcon}
                                        >
                                            View Covered Members
                                        </LoopButton>
                                        <StyledModularSummaryPlanCTASeperator />
                                        <LoopButton
                                            variant="text"
                                            size="small"
                                            onClick={handleViewDetails}
                                            iconSrc={ArrowRightEmerald}
                                            iconOrder="right"
                                        >
                                            View Details
                                        </LoopButton>
                                    </StyledModularSummaryPlanCTA>
                                </StyledModularSummaryPlanCTAWrapper>
                            </StyledModularSummaryPlanItemInfo>
                            <Typography variant="medium" weight="semiBold">
                                {formatAmount(summary.modular.price)}
                            </Typography>
                        </StyledModularSummaryPlanItem>
                        {summary.alacarte.length ? (
                            <>
                                <StyledModularSummaryHorizontalDivider />
                                <StyledSelectedPlanHeader>
                                    <Typography variant="small" color="secondary">
                                        Selected Benefits
                                    </Typography>
                                    <LoopButton
                                        variant="text"
                                        size="small"
                                        onClick={moveToBenefitsScreen}
                                        iconSrc={EditIconGreen}
                                    >
                                        Change Benefits Selection
                                    </LoopButton>
                                </StyledSelectedPlanHeader>

                                {summary.alacarte.map((m) => {
                                    const isPerLife = m.pricingStructure === BenefitPricingStructure.PER_LIFE;
                                    return (
                                        <StyledModularSummaryPlanItem key={m.id}>
                                            <StyledModularSummaryPlanItemInfo>
                                                <CardIcon imageUrl={m.logo} />
                                                <StyledModularSummaryColumn>
                                                    <StyledModularSummaryAlacarteText>
                                                        <Typography variant="medium" weight="semiBold">
                                                            {m.name}
                                                        </Typography>
                                                        <Typography variant="small" color="secondary">
                                                            {getModularSummaryAlacarteSubtext(m, familyBenefits[m.id])}
                                                        </Typography>
                                                        {isPerLife && (
                                                            <Tooltip
                                                                type="informative"
                                                                beak="top-center"
                                                                text={
                                                                    <StyledModularSummaryTooltip>
                                                                        <Typography
                                                                            variant="extraSmall"
                                                                            color="tertiary"
                                                                        >
                                                                            Selected Plans:
                                                                        </Typography>
                                                                        {familyBenefits[m.id]?.counts.map(
                                                                            ([name, count]) => (
                                                                                <StyledModularSummaryTooltipItem
                                                                                    key={name}
                                                                                >
                                                                                    <Typography
                                                                                        variant="small"
                                                                                        weight="semiBold"
                                                                                        color="tertiary"
                                                                                    >
                                                                                        {name}
                                                                                    </Typography>
                                                                                    <Typography
                                                                                        variant="extraSmall"
                                                                                        weight="medium"
                                                                                        color="tertiary"
                                                                                    >
                                                                                        {count}&nbsp;
                                                                                        {textPluralize('Member', count)}
                                                                                    </Typography>
                                                                                </StyledModularSummaryTooltipItem>
                                                                            )
                                                                        )}
                                                                    </StyledModularSummaryTooltip>
                                                                }
                                                            >
                                                                <InfoIcon
                                                                    width="20"
                                                                    height="20"
                                                                    strokeColor={theme.colors.secondaryText}
                                                                />
                                                            </Tooltip>
                                                        )}
                                                    </StyledModularSummaryAlacarteText>
                                                    <StyledModularSummaryPlanCTA>
                                                        {isPerLife && (
                                                            <>
                                                                <LoopButton
                                                                    variant="text"
                                                                    size="small"
                                                                    onClick={() => handleAlaCarteViewMembers(m)}
                                                                    iconSrc={PersonIcon}
                                                                    width="fit-content"
                                                                >
                                                                    View Covered Members
                                                                </LoopButton>
                                                                <StyledModularSummaryPlanCTASeperator />
                                                            </>
                                                        )}
                                                        <LoopButton
                                                            variant="text"
                                                            size="small"
                                                            onClick={() => handleViewAlacarteDetails(m)}
                                                            iconSrc={ArrowRightEmerald}
                                                            iconOrder="right"
                                                            width="fit-content"
                                                        >
                                                            View Details
                                                        </LoopButton>
                                                    </StyledModularSummaryPlanCTA>
                                                </StyledModularSummaryColumn>
                                            </StyledModularSummaryPlanItemInfo>
                                            <Typography variant="medium" weight="semiBold">
                                                {formatAmount(m.price)}
                                            </Typography>
                                        </StyledModularSummaryPlanItem>
                                    );
                                })}
                            </>
                        ) : null}

                        <StyledModularSummaryHorizontalDottedDivider />
                        <StyledModularSummaryPremiumWrapper>
                            <StyledModularSummaryPlanItem>
                                <Typography variant="small" weight="medium">
                                    Total Premium Amount
                                </Typography>
                                <Typography variant="medium" weight="semiBold">
                                    {formatAmount(summary.priceSummary.totalPremium)}
                                </Typography>
                            </StyledModularSummaryPlanItem>
                            {toUseWallet && (
                                <StyledModularSummaryPlanItem>
                                    <StyledModularSummaryPlanWallet>
                                        <Typography variant="small" weight="medium">
                                            Wallet Balance Used
                                        </Typography>
                                        <img src={WalletIcon} alt="wallet" />
                                    </StyledModularSummaryPlanWallet>
                                    <Typography variant="medium" weight="semiBold" color="error">
                                        - {formatAmount(summary.priceSummary.walletDeducted)}
                                    </Typography>
                                </StyledModularSummaryPlanItem>
                            )}
                        </StyledModularSummaryPremiumWrapper>
                        <StyledModularSummaryTotalHorizontalDivider />
                        <StyledModularSummaryPlanItem>
                            <StyledModularSummaryColumn>
                                <Typography variant="title2" weight="medium">
                                    To Pay
                                </Typography>
                                <Typography variant="small" color="secondary">
                                    (Inclusive of tax)
                                </Typography>
                            </StyledModularSummaryColumn>
                            <StyledModularSummaryColumnRight>
                                <Typography variant="title2" weight="medium">
                                    {formatAmount(summary.priceSummary.salaryDeducted)}
                                </Typography>
                            </StyledModularSummaryColumnRight>
                        </StyledModularSummaryPlanItem>
                        {summary.priceSummary.salaryDeducted && isPaymentInstallmentsEnabled ? (
                            <StyledEmiDeductionBox>
                                <CashIcon height="24" width="24" />
                                {hasInstallmentsText ? (
                                    <Typography variant="medium" color="emerald">
                                        {paymentInstallments}
                                    </Typography>
                                ) : (
                                    <>
                                        <Typography variant="medium" color="emerald">
                                            Will be deducted from your salary in
                                        </Typography>
                                        <Typography variant="medium" weight="bold" color="emerald">
                                            {installmentsCount} EMIs at {emiAmount}/month
                                        </Typography>
                                    </>
                                )}
                            </StyledEmiDeductionBox>
                        ) : null}
                        <PolicyBenefitsModal
                            isVisible={viewPlanDetails}
                            setIsVisible={setViewPlanDetails}
                            plan={summary.modular}
                        />
                        <BenefitDetailsModal
                            isVisible={!!viewBenefitDetails}
                            setIsVisible={() => setViewBenefitDetails(undefined)}
                            benefit={viewBenefitDetails}
                        />
                        <CoveredMembersModal
                            isVisible={!!viewCoveredMembers}
                            setIsVisible={() => setViewCoveredMembers(undefined)}
                            members={viewCoveredMembers?.family ?? []}
                            title="Covered Members"
                            subtitle={`Members covered in ${viewCoveredMembers?.name}`}
                            isLoading={isFamilyLoading}
                        />
                    </StyledModularEnrolmentSummaryCardContent>
                ) : null}
            </StyledModularEnrolmentSummaryCard>
        </StyledModularEnrolmentSummary>
    );
};

export default ModularEnrolmentSummary;
